/**
 * OAuth2 토큰 교환 관련 공통 유틸리티
 */

import {
  OAUTH_ENDPOINTS,
  OAUTH_CLIENTS
} from '@edusense/shared-constants';

// 중복 처리 방지를 위한 전역 상태
const ongoingExchanges = new Map<string, Promise<any>>();

export interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
  token_type?: string;
  scope?: string;
  error?: string;
  error_description?: string;
}

export interface OidcUserInfo {
  sub: string;
  name?: string;
  preferred_username?: string;
  picture?: string;
  email?: string;
}

export interface User {
  id: string;
  username: string;
  name?: string;
  email?: string;
  profileImage?: string;
  avatar?: string;
}

export interface TokenExchangeConfig {
  USE_PROXY?: boolean;
  AUTH_SERVER_URL?: string;
  API_SERVER_URL?: string;
}

/**
 * 토큰 교환 및 사용자 정보 획득을 처리하는 공통 함수
 */
export async function exchangeTokenAndFetchUserInfo(
  params: URLSearchParams,
  config?: TokenExchangeConfig
): Promise<{
  tokenData: TokenResponse;
  userInfo: OidcUserInfo;
  user: User;
}> {
  // 기본 설정
  const USE_PROXY = config?.USE_PROXY ?? true;
  const AUTH_SERVER_URL = config?.AUTH_SERVER_URL || 'https://auth.ugot.uk';

  // 중요: 프록시 토큰 엔드포인트는 항상 auth.ugot.uk를 사용해야 함
  // API_SERVER_URL이 아닌 AUTH_SERVER_URL을 사용
  const targetTokenUrl = USE_PROXY ? OAUTH_ENDPOINTS.proxyToken : OAUTH_ENDPOINTS.token;
  const USERINFO_ENDPOINT = OAUTH_ENDPOINTS.userinfo;

  console.log('[TokenExchange] Calling token endpoint:', targetTokenUrl);
  
  // 1. 토큰 교환
  const tokenResponse = await fetch(targetTokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
    // 프록시 엔드포인트는 CORS 정책으로 인해 credentials를 사용하지 않음
    // credentials: 'include' // 제거됨
  });

  if (!tokenResponse.ok) {
    const errorData = await tokenResponse.text();
    console.error('[TokenExchange] Token exchange failed:', errorData);
    throw new Error('토큰 교환 실패');
  }

  const tokenData = await tokenResponse.json();
  
  // Jackson 배열 형태 응답 처리
  let actualTokenData = tokenData;
  if (Array.isArray(tokenData) && tokenData.length === 2 && tokenData[0] === "java.util.LinkedHashMap") {
    console.log('[TokenExchange] Detected Jackson array format, extracting actual data');
    actualTokenData = tokenData[1];
  }

  // 오류 응답 처리
  if (actualTokenData && typeof actualTokenData === 'object' && 'error' in actualTokenData) {
    console.error('[TokenExchange] Token endpoint returned an error:', actualTokenData);
    const errorData = actualTokenData as { error: string, error_description?: string };
    if (errorData.error === 'invalid_grant') {
      throw new Error('토큰 교환 실패: 사용되었거나 유효하지 않은 인가 코드입니다.');
    }
    throw new Error(`토큰 교환 실패: ${errorData.error} - ${errorData.error_description || 'Unknown error'}`);
  }

  const { access_token, refresh_token, id_token, expires_in, token_type } = actualTokenData;
  
  if (!access_token) {
    throw new Error('토큰 응답에 액세스 토큰이 없습니다');
  }

  console.log('[TokenExchange] Token exchange successful:', {
    access_token: access_token.substring(0, 50) + '...',
    token_type,
    expires_in,
    has_refresh_token: !!refresh_token,
    refresh_token_length: refresh_token ? refresh_token.length : 0,
    has_id_token: !!id_token,
    id_token_length: id_token ? id_token.length : 0
  });
  
  // 🔍 DEBUG: Log full token response to understand what server sent
  console.log('[TokenExchange] 🔍 FULL TOKEN RESPONSE FROM SERVER:', {
    hasAccessToken: !!access_token,
    hasRefreshToken: !!refresh_token,
    refreshToken: refresh_token ? 'PRESENT' : 'MISSING',
    hasIdToken: !!id_token,
    scopes: actualTokenData.scope || 'no scope in response',
    tokenType: token_type || 'Bearer',
    expiresIn: expires_in
  });

  // 2. UserInfo 요청 (OAuth2 표준 방식)
  console.log('[TokenExchange] Fetching user info...');
  console.log('[TokenExchange] UserInfo endpoint:', USERINFO_ENDPOINT);
  
  // 🔍 중요: 토큰이 실제로 유효한지 확인
  if (!access_token || access_token.length < 10) {
    console.error('[TokenExchange] ❌ Invalid access_token:', {
      hasToken: !!access_token,
      tokenLength: access_token?.length || 0,
      tokenPrefix: access_token?.substring(0, 10) || 'null'
    });
    throw new Error('유효하지 않은 액세스 토큰입니다');
  }

  const authorizationHeader = `Bearer ${access_token}`;
  const userInfoRequestHeaders: HeadersInit = {
    'Accept': 'application/json',
    'Authorization': authorizationHeader
  };
  
  console.log('[TokenExchange] 🔍 Request details:');
  console.log('[TokenExchange]   - Method: GET');
  console.log('[TokenExchange]   - URL:', USERINFO_ENDPOINT);
  console.log('[TokenExchange]   - Authorization header:', `Bearer ${access_token.substring(0, 20)}...`);
  console.log('[TokenExchange]   - Full access_token length:', access_token.length);
  console.log('[TokenExchange]   - Headers object:', JSON.stringify(userInfoRequestHeaders, null, 2));
  console.log('[TokenExchange]   - Credentials:', 'omit');

  // 🚨 실제 HTTP 요청 전에 헤더 검증
  const headerValidation = {
    hasAcceptHeader: 'Accept' in userInfoRequestHeaders,
    hasAuthorizationHeader: 'Authorization' in userInfoRequestHeaders,
    authHeaderValue: userInfoRequestHeaders['Authorization'],
    authHeaderLength: userInfoRequestHeaders['Authorization']?.length || 0
  };
  console.log('[TokenExchange] 🔍 Header validation:', headerValidation);

  // 요청 직전 최종 확인
  console.log('[TokenExchange] 🚀 About to send userinfo request...');
  
  const userInfoResponse = await fetch(USERINFO_ENDPOINT, {
    method: 'GET',
    headers: userInfoRequestHeaders
    // OAuth2 Bearer 토큰 인증이므로 credentials 불필요
  });

  if (!userInfoResponse.ok) {
    const errorText = await userInfoResponse.text();
    console.error('[TokenExchange] ❌ UserInfo request failed:');
    console.error('[TokenExchange]   - Status:', userInfoResponse.status);
    console.error('[TokenExchange]   - Status Text:', userInfoResponse.statusText);
    console.error('[TokenExchange]   - Response Body:', errorText);
    console.error('[TokenExchange]   - Request URL:', USERINFO_ENDPOINT);
    console.error('[TokenExchange]   - Authorization header sent:', `Bearer ${access_token.substring(0, 20)}...`);
    
    // Response Headers 로깅 (서버에서 어떤 헤더를 보냈는지 확인)
    const responseHeaders: any = {};
    userInfoResponse.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });
    console.error('[TokenExchange]   - Response Headers:', responseHeaders);

    // 403 에러인 경우 특별히 분석
    if (userInfoResponse.status === 403) {
      console.error('[TokenExchange] 🔍 403 Forbidden Analysis:');
      console.error('[TokenExchange]   - This indicates the server received the request but denied access');
      console.error('[TokenExchange]   - Possible causes:');
      console.error('[TokenExchange]     1. Invalid or expired access token');
      console.error('[TokenExchange]     2. Missing Authorization header (even though we set it)');
      console.error('[TokenExchange]     3. Server authentication filter configuration issue');
      console.error('[TokenExchange]     4. Scope or permission issue');
      
      // 토큰이 실제로 Bearer 형태인지 확인
      const tokenStructureCheck = {
        startsWithBearer: authorizationHeader.startsWith('Bearer '),
        tokenAfterBearer: authorizationHeader.substring(7, 27) + '...',
        totalLength: authorizationHeader.length
      };
      console.error('[TokenExchange]   - Token structure check:', tokenStructureCheck);
    }

    throw new Error(`사용자 정보 조회 실패: ${userInfoResponse.status} ${userInfoResponse.statusText}`);
  }

  const userInfo = await userInfoResponse.json();

  // 보강: 서버 userinfo에 picture가 없을 경우 id_token 클레임에서 보완 시도
  try {
    if ((!userInfo || !userInfo.picture) && id_token && typeof id_token === 'string') {
      const parts = id_token.split('.');
      if (parts.length >= 2) {
        const payloadJson = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payloadJson && typeof payloadJson.picture === 'string' && payloadJson.picture.trim() !== '') {
          userInfo.picture = payloadJson.picture;
        }
        // 여유: 다른 기본 필드도 누락 시 보강
        if (!userInfo.preferred_username && typeof payloadJson.preferred_username === 'string') {
          userInfo.preferred_username = payloadJson.preferred_username;
        }
        if (!userInfo.name && typeof payloadJson.name === 'string') {
          userInfo.name = payloadJson.name;
        }
        if (!userInfo.email && typeof payloadJson.email === 'string') {
          userInfo.email = payloadJson.email;
        }
      }
    }
  } catch (e) {
    console.warn('[TokenExchange] ID 토큰에서 picture 보강 실패:', (e as any)?.message);
  }

  console.log('[TokenExchange] UserInfo received:', {
    sub: userInfo.sub,
    preferred_username: userInfo.preferred_username,
    email: userInfo.email,
    hasPicture: !!userInfo.picture
  });

  // 3. User 객체 생성
  const user: User = {
    id: userInfo.sub,
    username: userInfo.preferred_username || userInfo.email || userInfo.sub,
    name: userInfo.name,
    email: userInfo.email,
    profileImage: userInfo.picture,
    avatar: userInfo.picture
  };

  return {
    tokenData: actualTokenData,
    userInfo,
    user
  };
}

/**
 * Authorization Code를 사용한 토큰 교환 (중복 처리 방지 포함)
 */
export async function exchangeAuthorizationCode(
  code: string,
  codeVerifier: string,
  redirectUri: string,
  clientId: string = OAUTH_CLIENTS.APP_CLIENT.clientId,
  config?: TokenExchangeConfig
) {
  // 🚨 중요: 전달받은 파라미터 값 상세 로깅
  console.log('[TokenExchange] 🔍 exchangeAuthorizationCode 호출됨:', {
    codeLength: code?.length || 0,
    codePrefix: code?.substring(0, 20) + '...',
    codeVerifierLength: codeVerifier?.length || 0,
    codeVerifierPrefix: codeVerifier?.substring(0, 20) + '...',
    redirectUri,
    clientId,
    configUseProxy: config?.USE_PROXY
  });
  
  // 목업 데이터 감지
  if (code === 'mock-auth-code-12345' || codeVerifier === 'test-verifier-12345') {
    console.error('[TokenExchange] 🚨 MOCK DATA DETECTED!');
    console.error('[TokenExchange] 🚨 실제 OAuth 플로우가 아닌 테스트 데이터가 전달됨:', {
      code,
      codeVerifier
    });
  }
  
  // 중복 처리 방지: 동일한 코드로 진행 중인 교환이 있는지 확인
  const exchangeKey = `${code}_${clientId}`;
  
  if (ongoingExchanges.has(exchangeKey)) {
    console.log('[TokenExchange] Code exchange already in progress, waiting for existing operation');
    return ongoingExchanges.get(exchangeKey)!;
  }

  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
    client_id: clientId
  });
  
  console.log('[TokenExchange] 📤 Token exchange request params:', {
    grant_type: 'authorization_code',
    codeLength: code?.length,
    redirectUri,
    codeVerifierLength: codeVerifier?.length,
    clientId
  });

  // 진행 중인 교환 작업을 저장
  const exchangePromise = exchangeTokenAndFetchUserInfo(params, config)
    .finally(() => {
      // 완료 후 진행 중인 작업에서 제거
      ongoingExchanges.delete(exchangeKey);
    });

  ongoingExchanges.set(exchangeKey, exchangePromise);
  return exchangePromise;
}
